import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { AuthStateService } from '../../../../core/services/auth-state/auth-state-service.service';
import { AlertService } from '../../../../core/services/alert/alert.service';
import { AccountsService } from '../../../../core/services/accounts/accounts.service';
import { CampaignsService } from '../../../../core/services/campaigns/campaigns.service';

@Component({
  selector: 'app-broker-form',
  standalone: true,
  imports: [ReactiveFormsModule,
      TranslateModule,
      MatDatepickerModule,
      MatFormFieldModule,
      MatInputModule,
      CommonModule],
  templateUrl: './broker-form.component.html',
  styleUrl: './broker-form.component.css'
})
export class BrokerFormComponent {
    @Input() campaignData?: any;
    @Input() ladata: any = [];
    @Input() invoiceData: any = [];

    @Output() emitSubmit = new EventEmitter<string>();
    @Output() campaignUpdated = new EventEmitter<boolean>();

  data: any[] = [];
  form: FormGroup = this.fb.group({});
  totalComision: number = 0;
  selectedItems: any[] = [];
  account_type = this.authStateService.getAccountType();
  showScrollIndicator: boolean = false;
  brokerForm: FormGroup = new FormGroup({});

  brokerAccounts: any[] = [];
  filteredBrokerAccounts: any[] = [];
  selectedBroker: any = null;
  private brokersLoaded: boolean = false;

  @Input() hasBrokerPrincipal: boolean = false;
  @Output() excludedBrokersChanged = new EventEmitter<string[]>();

  @Input() set isEditMode(value: boolean) {
    const previousMode = this._isEditMode;
    this._isEditMode = value;
    if (previousMode === false && value === true) {
      this.selectedItems = [];
    }

    if (previousMode === true && value === false) {
      this.selectedItems = this.excludedBrokersExternal
        ? [...this.excludedBrokersExternal]
        : [];
    }
    if (previousMode !== value) {
      this.loadBrokerAccounts();
    }
  }

  constructor(
    private fb: FormBuilder,
    private authStateService: AuthStateService,
    private alertService: AlertService,
    private accountService: AccountsService,
    private campaignService: CampaignsService
  ) {}

  ngOnInit() {
    this.loadBrokerAccounts();
  }
  private previousEditMode: boolean = false;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['campaignData'] && changes['campaignData'].currentValue) {
      this.loadBrokerAccounts();
    }
    if (changes['isEditMode']) {
      const currentMode = changes['isEditMode'].currentValue;
      const previousMode = changes['isEditMode'].previousValue;
      if (previousMode === false && currentMode === true) {
        this.selectedItems = [];
      }

      this.previousEditMode = previousMode;
      this.loadBrokerAccounts();
    }

    if (changes['invoiceData']) {
      this.data = this.invoiceData.orders;
      this.totalComision = this.data.reduce(
        (sum, item) => sum + item.broker_commission_total,
        0
      );
    }
  }

  clearSelectionExternally(): void {
    this.selectedItems = [];
    if (!this.isEditMode) {
      this.emitExcludedBrokers();
    }
  }

  get safeEditMode(): boolean {
    if (!this.campaignData || Object.keys(this.campaignData).length === 0) {
      return false;
    }
    return this.isEditMode;
  }

  originalExcludedBrokers: string[] = [];

  loadBrokerAccounts(): void {
  this.alertService.loading('swal_messages.please_wait');

  this.accountService.getData().subscribe({
    next: (response: any) => {
      this.alertService.close();

      const allBrokers = response.data.filter(
        (account: any) => account.type === 'broker'
      );
      
      const sortedBrokers = allBrokers.sort((a: any, b: any) =>
        a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })
      );

      if (this.isEditMode && this.previousEditMode === false) {
        this.selectedItems = [];
        this.originalExcludedBrokers = [];
      }

      if (this.safeEditMode) {
        this.brokerAccounts = sortedBrokers;

        if (this.campaignData?.excluded_brokers?.length > 0) {
          const uniqueExcludedBrokers = [
            ...new Set<string>(this.campaignData.excluded_brokers),
          ];

          this.originalExcludedBrokers = [...uniqueExcludedBrokers];
          this.selectedItems = [...uniqueExcludedBrokers];
        } else {
          this.originalExcludedBrokers = [];
          this.selectedItems = [];
        }
      } else {
        this.brokerAccounts = sortedBrokers;
        this.originalExcludedBrokers = [];
      }

      this.filteredBrokerAccounts = [...this.brokerAccounts];
      this.removeDuplicatesFromSelection();
    },
    error: (error) => {
      console.error('❌ Error:', error);
      this.alertService.error('Error loading broker accounts');
    },
  });
}

  checkUpdateForm(): boolean {
    if (this.brokerForm.dirty) {
      return true;
    } else {
      return false;
    }
  }

  toggleSelection(broker: any): void {
    const brokerReference = broker.reference;
    const index = this.selectedItems.indexOf(brokerReference);
    if (index === -1) {
      this.selectedItems.push(brokerReference);
    } else {
      this.selectedItems.splice(index, 1);
    }

    this.removeDuplicatesFromSelection();
    if (!this.isEditMode) {
      this.emitExcludedBrokers();
    }
  }

  toggleSelectAll(event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;

    if (isChecked) {
      this.selectedItems = [
        ...new Set(
          this.filteredBrokerAccounts.map((broker) => broker.reference)
        ),
      ];
    } else {
      this.selectedItems = [];
    }
    if (!this.isEditMode) {
      this.emitExcludedBrokers();
    }
  }

  private removeDuplicatesFromSelection(): void {
    const before = this.selectedItems.length;
    const uniqueItems = [...new Set(this.selectedItems)];

    if (uniqueItems.length !== before) {
      console.warn('🔄 Se eliminaron duplicados:', {
        antes: before,
        después: uniqueItems.length,
      });
      this.selectedItems = uniqueItems;
    }
  }

  clearSelection(): void {
    this.alertService
      .confirmation(
        'swal_messages.confirm_clear_selection_campaigns',
        'swal_messages.warning',
        'swal_messages.campaigns.confirm',
        'swal_messages.campaigns.cancel'
      )
      .then((result) => {
        if (result.isConfirmed) {
          this.selectedItems = [];
          if (!this.isEditMode) {
            this.emitExcludedBrokers();
            this.alertService.success('Selección limpiada correctamente');
          } else {
            this.onSubmit();
          }
        } else {
          console.log('❌ Limpieza de selección cancelada por el usuario');
        }
      });
  }

  private emitExcludedBrokers(): void {
    this.excludedBrokersChanged.emit(this.selectedItems);
  }

  @Input() set excludedBrokersExternal(value: string[]) {
    if (!this.isEditMode) {
      const uniqueValues = value ? [...new Set(value)] : [];

      if (
        JSON.stringify(this.selectedItems.sort()) !==
        JSON.stringify(uniqueValues.sort())
      ) {
        this.selectedItems = uniqueValues;
      }
    }
  }

  onSubmit() {
    if (!this.isEditMode) {
      return;
    }

    if (!this.campaignData?.reference) {
      this.alertService.error('swal_messages.no_campaign_selected');
      return;
    }

    this.removeDuplicatesFromSelection();
    if (!this.hasExcludedBrokersChanged()) {
      return;
    }

    const changes = this.getChangedBrokers();

    const formResult = {
      excluded_brokers: [...new Set(this.selectedItems)],
    };

    this.alertService.loading('swal_messages.please_wait');

    this.campaignService
      .updateCampaignData(this.campaignData.reference, formResult)
      .subscribe({
        next: (data) => {
          if (data.data && data.data.excluded_brokers) {
            const excludedBrokers = Array.isArray(data.data.excluded_brokers)
              ? data.data.excluded_brokers.filter(
                  (item: any): item is string => typeof item === 'string'
                )
              : [];

            this.originalExcludedBrokers = [
              ...new Set<string>(excludedBrokers),
            ];
            this.selectedItems = [...this.originalExcludedBrokers];
            this.campaignData = { ...this.campaignData, ...data.data };
          }

          this.alertService
            .successOk('swal_messages.campaign_updated')
            .then((result) => {
              if (result.isConfirmed) {
                this.loadBrokerAccounts();
                this.campaignUpdated.emit(true);
                this.emitSubmit.emit('brokers_updated');
              }
            });
        },
        error: (error) => {
          this.alertService.error(error.error).then((result) => {
            if (result.isConfirmed && error.code == 409) {
              window.location.reload();
            }
          });
        },
      });
  }

  hasExcludedBrokersChanged(): boolean {
    const current = [...new Set(this.selectedItems || [])].sort();
    const original = [...new Set(this.originalExcludedBrokers || [])].sort();
    const hasChanged = JSON.stringify(current) !== JSON.stringify(original);

    return hasChanged;
  }

  getChangedBrokers(): { added: string[]; removed: string[] } {
    const currentSet = new Set(this.selectedItems);
    const originalSet = new Set(this.originalExcludedBrokers);

    const added = this.selectedItems.filter(
      (broker) => !originalSet.has(broker)
    );
    const removed = this.originalExcludedBrokers.filter(
      (broker) => !currentSet.has(broker)
    );
    return { added, removed };
  }

  areAllSelected(): boolean {
    if (this.filteredBrokerAccounts.length === 0) {
      return false;
    }

    const uniqueSelected = new Set(this.selectedItems);
    const uniqueBrokers = new Set(
      this.filteredBrokerAccounts.map((broker) => broker.reference)
    );

    return (
      uniqueSelected.size === uniqueBrokers.size &&
      Array.from(uniqueSelected).every((broker) => uniqueBrokers.has(broker))
    );
  }

  isSelected(broker: any): boolean {
    return this.selectedItems.includes(broker.reference);
  }

  checkScroll(event: any) {
    const element = event.target;
    const atBottom =
      element.scrollHeight - element.scrollTop === element.clientHeight;

    if (this.filteredBrokerAccounts.length > 12) {
      this.showScrollIndicator = !atBottom;
    }
  }

  getBadgeColor(stage: string, priority: string): string {
    const statusColorMap: { [key: string]: string } = {
      new: 'text-new',
      used: 'text-used',
      invoice: 'text-no-action',
    };

    const priorityColor = statusColorMap[priority];
    return priorityColor || 'text-priority-0';
  }

  private _isEditMode: boolean = false;

  get isEditMode(): boolean {
    return this._isEditMode;
  }
}
